<template>
  <header class="apple-glass-header sticky top-0 z-40">
    <div class="header-content mx-auto min-w-0 px-4 sm:px-6 lg:px-8" :class="{ 'home-header-content': route.name === 'home' }">
      <div class="flex min-w-0 justify-between items-center h-16">
        <!-- Logo and Brand -->
        <div class="flex shrink-0 items-center">
          <router-link :to="isAuthenticated ? '/discover' : '/'" class="brand-lockup" aria-label="SugarM">
            <span class="brand-seal" aria-hidden="true"><img src="/assets/brand/sugarm-monogram.svg" alt="" /></span>
            <img class="brand-wordmark" src="/assets/brand/sugarm-wordmark.svg" alt="" />
          </router-link>
        </div>

        <!-- Desktop Navigation -->
        <nav v-if="isAuthenticated" class="hidden md:flex items-center space-x-8">
          <router-link
              v-for="item in navigationItems"
              :key="item.name"
              :to="item.to"
              class="nav-link"
              :class="{
              'text-brand-accent dark:text-brand-accent-dark': $route.path === item.to,
              'text-gray-700 dark:text-gray-300 hover:text-brand-accent dark:hover:text-brand-accent-dark': $route.path !== item.to
            }"
          >
            <component :is="item.icon" class="w-5 h-5 mr-2"/>
            {{ $t(item.label) }}
          </router-link>
        </nav>

        <!-- User Menu -->
        <div class="flex min-w-0 items-center space-x-2 sm:space-x-4">
          <!-- Language Selector -->
          <div class="relative language-control" ref="languageMenuRef">
            <button
                @click="toggleLanguageMenu"
                class="language-trigger"
                :aria-expanded="showLanguageMenu"
                aria-haspopup="menu"
            >
              <LanguageIcon class="w-4 h-4"/>
              <span class="hidden lg:block">{{ currentLanguageDisplay }}</span>
              <span class="hidden sm:block lg:hidden">{{ currentLanguageCode }}</span>
              <ChevronDownIcon class="w-3 h-3"/>
            </button>

            <!-- Language Dropdown -->
            <div
                v-if="showLanguageMenu"
                class="language-menu"
                role="menu"
            >
              <button
                  v-for="lang in availableLanguages"
                  :key="lang.code"
                  @click="changeLanguage(lang.code)"
                  class="language-option"
                  :class="{ 'is-active': currentLanguage === lang.code }"
                  role="menuitem"
              >
                <span class="language-code">{{ lang.short }}</span>
                {{ lang.name }}
              </button>
            </div>
          </div>


          <!-- VIP Badge -->
          <div v-if="isVip"
               class="hidden sm:flex items-center bg-gradient-to-r from-yellow-400 to-yellow-600 text-white px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium">
            <StarIcon class="w-3 sm:w-4 h-3 sm:h-4 mr-1"/>
            <span class="hidden md:block">{{ $t('subscription.vipMember') }}</span>
            <span class="md:hidden">VIP</span>
          </div>

          <!-- Account hub -->
          <router-link
              v-if="isAuthenticated"
              to="/profile"
              class="flex items-center p-1 rounded-full hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brass-400 transition-colors"
              :aria-label="$t('navigation.myProfile')"
            >
              <div class="w-8 h-8 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700">
                <img
                    v-if="userAvatar"
                    :src="userAvatar"
                    :alt="user?.display_name"
                    class="w-full h-full object-cover"
                />
                <div v-else class="w-full h-full flex items-center justify-center text-gray-400">
                  <UserIcon class="w-5 h-5"/>
                </div>
              </div>
          </router-link>

          <!-- Auth Buttons -->
          <div v-else class="flex items-center space-x-1 sm:space-x-3">
            <router-link to="/auth/login" class="home-login">
              {{ $t('auth.login') }}
            </router-link>
            <router-link to="/auth/register" class="home-register">
              {{ $t('auth.register') }}
            </router-link>
          </div>

        </div>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import {computed, onMounted, onUnmounted, ref} from 'vue'
import {useRoute, useRouter} from 'vue-router'
import {useI18n} from 'vue-i18n'
import {useAuthStore} from '@/stores/auth'
import {fixImageUrl} from '@/services/api'
import {
  ChatBubbleLeftRightIcon,
  ChevronDownIcon,
  GlobeAltIcon as LanguageIcon,
  MagnifyingGlassIcon,
  StarIcon,
  UserIcon
} from '@heroicons/vue/24/outline'

// Composables
useRouter()
const route = useRoute()
const authStore = useAuthStore()
const {locale} = useI18n()

// Reactive data
const showLanguageMenu = ref(false)
const isDarkMode = ref(false)
const languageMenuRef = ref<HTMLElement>()

// Computed properties
const isAuthenticated = computed(() => authStore.isAuthenticated)
const isVip = computed(() => authStore.isVip)
const user = computed(() => authStore.user)

const userAvatar = computed(() => {
  const photos = [
    ...(user.value?.photos || []),
    ...(user.value?.profile?.photos || [])
  ].filter(photo => photo.status === 1 && !photo.is_private)
  const primaryPhoto = photos.find(photo => photo.is_cover || photo.is_primary) || photos[0]
  const url = primaryPhoto?.thumbnail_url || primaryPhoto?.url || primaryPhoto?.original_url
  return url ? fixImageUrl(url) : null
})

// Language management
const availableLanguages = [
  {code: 'zh', name: '繁體中文', short: 'ZH'},
  {code: 'en', name: 'English', short: 'EN'},
  {code: 'th', name: 'ภาษาไทย', short: 'TH'},
  {code: 'hi', name: 'हिन्दी', short: 'HI'},
  {code: 'sw', name: 'Kiswahili', short: 'SW'},
  {code: 'fr', name: 'Français', short: 'FR'}
]

const currentLanguage = computed(() => locale.value)
const currentLanguageDisplay = computed(() => {
  const lang = availableLanguages.find(l => l.code === currentLanguage.value)
  return lang?.name || 'Language'
})

const currentLanguageCode = computed(() => {
  const lang = availableLanguages.find(l => l.code === currentLanguage.value)
  return lang?.short || 'LANG'
})

// Navigation items
const navigationItems = computed(() => [
  {name: 'discover', to: '/discover', label: 'navigation.discover', icon: MagnifyingGlassIcon},
  {name: 'wish-tree', to: '/wish-tree', label: 'navigation.wishTree', icon: StarIcon},
  {name: 'messages', to: '/messages', label: 'navigation.messages', icon: ChatBubbleLeftRightIcon},
  {name: 'subscription', to: '/subscription', label: 'navigation.subscription', icon: StarIcon}
])

// Methods
const toggleLanguageMenu = () => {
  showLanguageMenu.value = !showLanguageMenu.value
}

const changeLanguage = async (langCode: string) => {
  console.log('HeaderNavigation: Switching to', langCode)

  try {
    // 使用 i18n 的 setLanguage 函數來正確切換語言
    const {setLanguage} = await import('@/i18n')
    const success = await setLanguage(langCode)

    if (success) {
      localStorage.setItem('language', langCode)
      showLanguageMenu.value = false
      console.log('Language switched successfully to:', langCode)
    } else {
      console.error('Failed to switch language to:', langCode)
    }
  } catch (error) {
    console.error('Error switching language:', error)
    // 回退到直接設定
    locale.value = langCode
    localStorage.setItem('language', langCode)
  }
}

// Close menus when clicking outside
const handleClickOutside = (event: Event) => {
  if (languageMenuRef.value && !languageMenuRef.value.contains(event.target as Node)) {
    showLanguageMenu.value = false
  }
}

// Lifecycle
onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  // Initialize dark mode from localStorage or system preference
  const savedTheme = localStorage.getItem('theme')
  if (savedTheme) {
    isDarkMode.value = savedTheme === 'dark'
  } else {
    isDarkMode.value = window.matchMedia('(prefers-color-scheme: dark)').matches
  }
  if (isDarkMode.value) {
    document.documentElement.classList.add('dark')
  } else {
    document.documentElement.classList.remove('dark')
  }
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.apple-glass-header {
  border-bottom: 1px solid rgba(184, 137, 79, 0.32);
  background: rgba(17, 9, 12, 0.96);
  -webkit-backdrop-filter: saturate(120%) blur(14px);
  backdrop-filter: saturate(120%) blur(14px);
}

.header-content {
  max-width: 80rem;
}

.header-content.home-header-content {
  max-width: 1440px;
}

@supports not ((-webkit-backdrop-filter: blur(1px)) or (backdrop-filter: blur(1px))) {
  .apple-glass-header {
    background: rgba(33, 14, 20, 0.94);
  }
}

.brand-lockup {
  display: inline-flex;
  align-items: center;
  color: #fff8ed;
  text-decoration: none;
}

.brand-wordmark {
  display: block;
  width: 6.5rem;
  height: auto;
}

.brand-seal {
  display: none;
  width: 2rem;
  height: 2rem;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 239, 208, 0.34);
  border-radius: 0.75rem;
  background: linear-gradient(145deg, rgba(201, 157, 91, 0.96), rgba(143, 99, 52, 0.96));
  box-shadow: inset 0 1px 0 rgba(255, 249, 232, 0.2), 0 6px 18px rgba(0, 0, 0, 0.18);
}

.brand-seal img {
  display: block;
  width: 1.15rem;
  height: auto;
}

@media (max-width: 639px) {
  .brand-wordmark {
    display: block;
    width: 5.25rem;
  }

  .brand-seal {
    display: none;
  }

  .home-register {
    min-height: 36px;
    padding-inline: .7rem;
    font-size: .75rem;
  }
}

.nav-link {
  @apply flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors;
}

.language-trigger {
  display: inline-flex;
  min-height: 44px;
  align-items: center;
  gap: .5rem;
  border: 0;
  border-radius: 3px;
  padding: 0 .55rem;
  background: transparent;
  color: #c9a77a;
  font-size: .8125rem;
  font-weight: 600;
  transition: color 160ms ease, background-color 160ms ease;
}

.language-trigger:hover,
.language-trigger[aria-expanded="true"] {
  background: rgba(184, 137, 79, .08);
  color: #f1dfc1;
}

.language-trigger:focus-visible,
.home-login:focus-visible,
.home-register:focus-visible,
.language-option:focus-visible {
  outline: 3px solid rgba(232, 198, 143, .42);
  outline-offset: 3px;
}

.language-menu {
  position: absolute;
  z-index: 50;
  top: calc(100% + .5rem);
  right: 0;
  width: 12rem;
  padding: .35rem;
  border: 1px solid rgba(184, 137, 79, .42);
  border-radius: 3px;
  background: #210e14;
  box-shadow: 0 18px 36px rgba(0, 0, 0, .38);
}

.language-option {
  display: flex;
  min-height: 42px;
  width: 100%;
  align-items: center;
  gap: .75rem;
  border: 0;
  border-radius: 2px;
  padding: 0 .75rem;
  background: transparent;
  color: #c9a77a;
  font-size: .8125rem;
  text-align: left;
}

.language-option:hover,
.language-option.is-active {
  background: rgba(184, 137, 79, .1);
  color: #f1dfc1;
}

.language-code {
  width: 1.65rem;
  color: #b8894f;
  font-size: .6875rem;
  font-weight: 700;
  letter-spacing: .08em;
}

.home-login,
.home-register {
  display: inline-flex;
  min-height: 38px;
  align-items: center;
  justify-content: center;
  border-radius: 3px;
  padding: 0 .85rem;
  font-size: .8125rem;
  font-weight: 700;
  text-decoration: none;
}

.home-login {
  color: #c9a77a;
  transition: color 160ms ease;
}

.home-login:hover { color: #f1dfc1; }

.home-register {
  border: 1px solid #b8894f;
  background: transparent;
  color: #e8c68f;
  transition: border-color 160ms ease, background-color 160ms ease, color 160ms ease;
}

.home-register:hover {
  border-color: #e8c68f;
  background: rgba(184, 137, 79, .1);
  color: #f1dfc1;
}

.home-menu-toggle {
  display: grid;
  width: 44px;
  height: 44px;
  place-items: center;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 3px;
  transition: border-color 160ms ease, background-color 160ms ease, color 160ms ease;
}

.home-menu-toggle:hover {
  border-color: rgba(184, 137, 79, .28);
  background: rgba(184, 137, 79, .08);
}

.home-menu-toggle:focus-visible {
  outline: 3px solid rgba(232, 198, 143, .42);
  outline-offset: 3px;
}

.btn {
  @apply inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2;
}

.btn-ghost {
  @apply text-ivory-50 hover:bg-ink-800 focus:ring-brass-600;
}

.btn-primary {
  @apply text-white bg-button-cta hover:bg-brand-accent focus:ring-brand-accent;
}

.btn-sm {
  @apply px-3 py-1.5 text-xs;
}
</style>
